import { CardContent, Separator } from '@ais-ui/ui-core';
import { ShipInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import { useLengthUnits } from '../../Units/useLengthUnits';

interface VesselInfoTooltipProps {
  vesselInfo: ShipInfo;
}

interface VesselInfoTooltipItemProps {
  label: string;
  value: string;
}

const VesselInfoTooltipItem = ({
  label,
  value,
}: VesselInfoTooltipItemProps) => {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 max-w-[12rem]">
      <p className="text-xs font-medium flex-none text-left max-w-[6rem]">
        {label}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 flex-grow text-right">
        {value ? value : '-'}
      </p>
    </div>
  );
};

export const VesselInfoTooltip = ({ vesselInfo }: VesselInfoTooltipProps) => {
  const { settings } = useSettings();
  const { convertLengthString } = useLengthUnits();
  // Check if all settings are falsy
  const ribbonSettings = settings['ribbonPlugin.settings'];
  const hasVisibleItems =
    (ribbonSettings?.show_vessel_ais_type && vesselInfo.aisType) ||
    (ribbonSettings?.show_vessel_imo && vesselInfo.imo) ||
    (ribbonSettings?.show_vessel_mmsi && vesselInfo.mmsi) ||
    (ribbonSettings?.show_vessel_callsign && vesselInfo.callSign) ||
    (ribbonSettings?.show_vessel_shiptype && vesselInfo.shipType) ||
    (ribbonSettings?.show_vessel_cargotype && vesselInfo.cargoType) ||
    (ribbonSettings?.show_vessel_country && vesselInfo.country) ||
    (ribbonSettings?.show_vessel_destination && vesselInfo.destination) ||
    (ribbonSettings?.show_vessel_size &&
      (vesselInfo.width || vesselInfo.length)) ||
    (ribbonSettings?.show_vessel_sog && vesselInfo.speedOverGround) ||
    (ribbonSettings?.show_vessel_cog && vesselInfo.courseOverGround) ||
    (ribbonSettings?.show_vessel_heading && vesselInfo.heading) ||
    (ribbonSettings?.show_vessel_draught && vesselInfo.draught) ||
    (ribbonSettings?.show_vessel_navstatus && vesselInfo.navigationalStatus);

  // Return null if no items will be shown
  if (!hasVisibleItems) {
    return null;
  }

  return (
    <CardContent className="pt-0 pb-2 -mt-1 space-y-2 px-3">
      <Separator className="-ml-1"></Separator>
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
        {ribbonSettings?.show_vessel_ais_type && vesselInfo.aisType && (
          <VesselInfoTooltipItem
            key="aisType"
            label="AIS"
            value={vesselInfo.aisType}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_imo && vesselInfo.imo && (
          <VesselInfoTooltipItem
            key="imo"
            label="IMO"
            value={vesselInfo.imo}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_mmsi && vesselInfo.mmsi && (
          <VesselInfoTooltipItem
            key="mmsi"
            label="MMSI"
            value={vesselInfo.mmsi}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_callsign && vesselInfo.callSign && (
          <VesselInfoTooltipItem
            key="callsign"
            label="Callsign"
            value={vesselInfo.callSign}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_shiptype && vesselInfo.shipType && (
          <VesselInfoTooltipItem
            key="shipType"
            label="Ship type"
            value={vesselInfo.shipType}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_cargotype && vesselInfo.cargoType && (
          <VesselInfoTooltipItem
            key="cargoType"
            label="Cargo type"
            value={vesselInfo.cargoType}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_country && vesselInfo.country && (
          <VesselInfoTooltipItem
            key="country"
            label="Country"
            value={vesselInfo.country}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_destination && vesselInfo.destination && (
          <VesselInfoTooltipItem
            key="destination"
            label="Destination"
            value={vesselInfo.destination}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_size && vesselInfo.width && (
          <VesselInfoTooltipItem
            key="width"
            label="Width"
            value={convertLengthString(vesselInfo.width)}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_size && vesselInfo.length && (
          <VesselInfoTooltipItem
            key="length"
            label="Length"
            value={convertLengthString(vesselInfo.length)}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_sog && vesselInfo.speedOverGround && (
          <VesselInfoTooltipItem
            key="sog"
            label="SOG"
            value={vesselInfo.speedOverGround}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_cog && vesselInfo.courseOverGround && (
          <VesselInfoTooltipItem
            key="cog"
            label="COG"
            value={vesselInfo.courseOverGround}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_heading && vesselInfo.heading && (
          <VesselInfoTooltipItem
            key="heading"
            label="Heading"
            value={vesselInfo.heading}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_draught && vesselInfo.draught && (
          <VesselInfoTooltipItem
            key="draught"
            label="Draught"
            value={convertLengthString(vesselInfo.draught)}
          ></VesselInfoTooltipItem>
        )}
        {ribbonSettings?.show_vessel_navstatus &&
          vesselInfo.navigationalStatus && (
            <VesselInfoTooltipItem
              key="navStatus"
              label="Navigational status"
              value={vesselInfo.navigationalStatus}
            ></VesselInfoTooltipItem>
          )}
      </div>
    </CardContent>
  );
};
